// L'assistant « Nouvelle saison » (D-143, D-115 ; maquettes a1 à a4) : les équipes de la saison prochaine proposées d'après
// l'âge de chacun (la catégorie de la saison prochaine, calculée comme l'import FBI : expectedCategory) et les projets du
// bilan. Logique pure, testée (nouvelle-saison.test.ts).
//  * Rien n'est imposé : chaque personne se déplace d'un toucher (`moves`), chaque suggestion s'accepte ou non (`decisions`).
//  * Les suggestions qui touchent aux règles FFBB (entente, équipe mixte, surclassement, regroupement de deux catégories)
//    restent des PROPOSITIONS au bureau, au texte prudent (« si ton comité l'accepte ») : rien n'est vérifié auprès de la FFBB.
//  * Accords : agree() d'après le genre de l'équipe (D-008).
import { CATS, expectedCategory, newTeamName, type Cat } from './import';
import { agree, playerNoun, type TeamGender } from './labels';
import type { Project } from './bilan';

export interface NsPerson {
  person_id: string; first_name: string; nickname: string | null; birth_date: string | null; gender: 'F' | 'M' | null; team_id: string;
  project: Project | null; project_note: string | null; by_parent?: boolean; minor?: boolean;
}
export interface NsCoach { user_id: string; name: string; role: string }
export interface NsTeam { team_id: string; name: string; category: string | null; gender: TeamGender; fee_cents: number | null; coaches: NsCoach[] }
export type Decision = 'accepted' | 'declined';

export interface NsRow { person_id: string; name: string; project: Project | null; note: string | null; up: boolean; fromTeam: string; moved: boolean }
export interface NsAction { key: string; label: string; primary?: boolean; href?: string }
export interface NsWarn { key: string; text: string; actions: NsAction[]; decided: Decision | null; choice: string | null }
export interface NsGroup {
  key: string; name: string; category: string; gender: TeamGender; isNew: boolean;
  coach: { user_id: string; name: string } | null; coachSure: boolean; fee_cents: number | null;
  sub: string; from: string; rows: NsRow[]; sure: number; maybe: number; warn: NsWarn | null; merged: boolean;
}
export interface NsSuggestion { key: string; title: string; text: string; into: string; from: string; decided: Decision | null }
export interface NsProposal {
  groups: NsGroup[]; out: NsRow[]; suggestions: NsSuggestion[];
  totals: { teams: number; players: number; up: number };
  /** Ce qui reste à décider (« U17 filles : à décider »). */
  open: { key: string; title: string; text: string }[];
}

const nameOf = (p: Pick<NsPerson, 'first_name' | 'nickname'>) => (p.nickname?.trim() || p.first_name).trim();
const rank = (c: string) => (CATS as readonly string[]).indexOf(c);
const YOUNG = new Set(['U7', 'U9', 'U11']);
const sureOf = (p: Project | null) => p === 'continue' || p === 'change';
/** « Je déménage à Toulouse en août. Merci… » → « déménage à Toulouse en août » : la raison, courte. */
const shortNote = (n: string) => { const s = n.split(/[.!?]/)[0]!.replace(/^(je|il|elle)\s+/i, '').trim(); return s.length > 28 ? `${s.slice(0, 27)}…` : s; };

/** La catégorie de la saison prochaine d'une personne (sans date de naissance : celle de son équipe ; U20 et plus : Seniors
 *  quand le club n'a pas d'équipe U20 ; U17 → U18 quand le club a des U18 et pas de U17). */
export function targetCategory(p: Pick<NsPerson, 'birth_date'>, team: Pick<NsTeam, 'category'> | undefined, endYear: number, clubCats: ReadonlySet<string>): string {
  const cur = team?.category ?? 'Seniors';
  if (cur === 'Seniors' || cur === 'Loisir') return cur;
  if (!p.birth_date) return cur;
  let c: string = expectedCategory(p.birth_date, endYear);
  if (c === 'U20' && !clubCats.has('U20')) c = 'Seniors';
  if (c === 'U17' && clubCats.has('U18') && !clubCats.has('U17')) c = 'U18';
  return c;
}

/** Le sexe du groupe : mixte chez les petits quand le club y a une équipe mixte (ou rien) ; sinon celui de la personne, de
 *  son équipe, ou mixte. */
function groupGender(p: NsPerson, team: NsTeam | undefined, cat: string, teams: readonly NsTeam[]): TeamGender {
  const g: TeamGender = p.gender ?? (team && team.gender !== 'X' ? team.gender : 'X');
  if (YOUNG.has(cat)) {
    const same = teams.filter(t => t.category === cat);
    if (!same.length || same.some(t => t.gender === 'X')) return 'X';
  }
  return g;
}

const born = (rows: { birth: string | null }[], g: TeamGender, cat: string) => {
  if (cat === 'Seniors' || cat === 'Loisir') return 'Adultes';
  const ys = [...new Set(rows.map(r => r.birth?.slice(0, 4)).filter((y): y is string => !!y))].sort();
  if (!ys.length) return cat;
  const word = agree(g, 'Nés', 'Nées');
  return ys.length === 1 ? `${word} en ${ys[0]}` : `${word} en ${ys.slice(0, -1).join(', ')} et ${ys.at(-1)}`;
};

const headCoach = (t: NsTeam | undefined) => (t ? t.coaches.find(c => c.role === 'head_coach') ?? t.coaches[0] ?? null : null);

/** Les équipes de la saison prochaine : `season` = la saison préparée (« 2027-2028 »). */
export function proposeTeams(people: readonly NsPerson[], teams: readonly NsTeam[], season: string,
  moves: Readonly<Record<string, string>> = {}, decisions: Readonly<Record<string, Decision | string>> = {}): NsProposal {
  const endYear = Number(season.slice(5, 9));
  const teamById = new Map(teams.map(t => [t.team_id, t]));
  const clubCats = new Set(teams.map(t => t.category).filter((c): c is string => !!c));
  type Item = NsRow & { cat: string; g: TeamGender; birth: string | null; team?: NsTeam };
  const out: NsRow[] = [];
  const items: Item[] = [];
  for (const p of people) {
    const team = teamById.get(p.team_id);
    const row: NsRow = { person_id: p.person_id, name: nameOf(p), project: p.project, note: p.project_note, up: false, fromTeam: team?.name ?? '', moved: false };
    if (p.project === 'stop') { out.push(row); continue; }
    const cat = targetCategory(p, team, endYear, clubCats);
    const g = groupGender(p, team, cat, teams);
    items.push({ ...row, up: !!team?.category && team.category !== 'Seniors' && cat !== team.category, cat, g, birth: p.birth_date, team });
  }
  const keyOf = (cat: string, g: TeamGender) => `${cat}|${g}`;
  const groups = new Map<string, { cat: string; g: TeamGender; items: Item[] }>();
  const put = (k: string, cat: string, g: TeamGender, it: Item) => { if (!groups.has(k)) groups.set(k, { cat, g, items: [] }); groups.get(k)!.items.push(it); };
  for (const it of items) put(keyOf(it.cat, it.g), it.cat, it.g, it);

  // --- Les décisions du bureau (acceptées) : regrouper deux catégories, jouer en catégorie au-dessus, une équipe mixte ---
  const accepted = (k: string) => decisions[k] === 'accepted';
  const moveAll = (fromK: string, toK: string, toCat: string, toG: TeamGender) => {
    const f = groups.get(fromK);
    if (!f || fromK === toK) return;
    for (const it of f.items) put(toK, toCat, toG, it);
    groups.delete(fromK);
  };
  for (const [k, v] of Object.entries(decisions)) {
    if (v !== 'accepted') continue;
    const [kind, a, b] = k.split(':');
    if ((kind === 'merge' || kind === 'older') && a && b && groups.has(a)) {
      const [cat, g] = b.split('|') as [string, TeamGender];
      moveAll(a, b, cat, g);
    } else if (kind === 'mixte' && a) {
      for (const g of ['F', 'M'] as TeamGender[]) moveAll(keyOf(a, g), keyOf(a, 'X'), a, 'X');
    }
  }
  // --- Les déplacements d'une personne, d'un toucher (une clé de groupe : « U15|F ») ---
  for (const [pid, to] of Object.entries(moves)) {
    for (const [k, gr] of groups) {
      const i = gr.items.findIndex(x => x.person_id === pid);
      if (i < 0 || k === to) continue;
      const [it] = gr.items.splice(i, 1);
      const [cat, g] = to.split('|') as [string, TeamGender];
      put(to, cat, (g ?? 'X') as TeamGender, { ...it!, moved: true });
      if (!gr.items.length) groups.delete(k);
      break;
    }
  }

  // --- Chaque groupe : son nom (celui de l'équipe de cette catégorie, sinon un nouveau), son coach, son tarif ---
  const list: NsGroup[] = [];
  const sortKey = (cat: string, g: TeamGender) => (cat === 'Seniors' ? -100 : cat === 'Loisir' ? -90 : -rank(cat)) * 10 + (g === 'F' ? 0 : g === 'M' ? 1 : 2);
  for (const [key, gr] of [...groups].sort((a, b) => sortKey(a[1].cat, a[1].g) - sortKey(b[1].cat, b[1].g))) {
    const same = teams.filter(t => t.category === gr.cat && (t.gender === gr.g || (gr.g === 'X' && YOUNG.has(gr.cat))));
    const isNew = same.length === 0;
    const merged = same.length > 1;
    const name = same.length === 1 ? same[0]!.name : newTeamName(gr.cat, gr.g);
    // Le coach : celui de l'équipe de la catégorie ; une nouvelle équipe : le coach d'où vient la plupart (« suit ses joueurs ? »).
    const from = new Map<string, number>();
    for (const it of gr.items) if (it.team) from.set(it.team.team_id, (from.get(it.team.team_id) ?? 0) + 1);
    const mainFrom = [...from].sort((a, b) => b[1] - a[1])[0]?.[0];
    const own = same.length ? headCoach(same[0]) : null;
    const hint = !own && mainFrom ? headCoach(teamById.get(mainFrom)) : null;
    const coach = own ?? hint;
    const fee = (same.find(t => t.fee_cents != null)?.fee_cents) ?? (mainFrom ? teamById.get(mainFrom)?.fee_cents ?? null : null);
    const stay = gr.items.filter(it => it.team && same.some(t => t.team_id === it.team!.team_id)).length;
    const upFrom = [...new Set(gr.items.filter(it => it.team && !same.some(t => t.team_id === it.team!.team_id)).map(it => it.team!.name))];
    const nUp = gr.items.length - stay;
    const quit = out.filter(r => same.some(t => t.name === r.fromTeam));
    const fromText = [quit.length === 1 ? `${quit[0]!.name} arrête${quit[0]!.note ? ` (${shortNote(quit[0]!.note)})` : ''}` : quit.length ? `${quit.map(r => r.name).join(', ')} arrêtent` : '',
      stay ? `${stay} ${stay > 1 ? 'restent' : 'reste'}` : '', nUp && upFrom.length ? `${nUp} ${nUp > 1 ? 'montent' : 'monte'} des ${upFrom.join(' et ')}` : '',
      merged ? `les ${same.length} équipes n’en font plus qu’une` : ''].filter(Boolean).join(' · ');
    const rows: NsRow[] = gr.items.map(({ cat: _c, g: _g, birth: _b, team: _t, ...r }) => r)
      .sort((a, b) => Number(b.up) - Number(a.up) || a.name.localeCompare(b.name, 'fr'));
    const sure = rows.filter(r => sureOf(r.project)).length;
    list.push({
      key, name, category: gr.cat, gender: gr.g, isNew, merged, coach: coach ? { user_id: coach.user_id, name: coach.name } : null, coachSure: !!own && !merged,
      fee_cents: fee, rows, sure, maybe: rows.length - sure, warn: null,
      sub: `${born(gr.items, gr.g, gr.cat)}${coach ? ` · coach ${coach.name}${own && !merged ? '' : ' ?'}` : ' · sans coach'}`, from: fromText,
    });
  }

  // --- Les petits groupes : moins de 5, une proposition prudente ---
  const byKey = new Map(list.map(g => [g.key, g]));
  const older = (g: NsGroup) => list.filter(o => o.gender === g.gender && o.key !== g.key && (o.category === 'Seniors' || rank(o.category) > rank(g.category)))
    .sort((a, b) => (a.category === 'Seniors' ? 99 : rank(a.category)) - (b.category === 'Seniors' ? 99 : rank(b.category)))[0];
  for (const g of list) {
    const n = g.rows.length;
    if (n >= 5 || g.category === 'Seniors') continue;
    const word = playerNoun(g.gender, n);
    const opp = g.gender !== 'X' && rank(g.category) <= rank('U13') ? byKey.get(`${g.category}|${g.gender === 'F' ? 'M' : 'F'}`) : undefined;
    const up = older(g);
    const actions: NsAction[] = [];
    let text: string;
    if (opp) {
      text = `${n} ${word} : il en faut 5. Les essais de juin, ou une ${g.category} mixte avec les ${opp.gender === 'M' ? 'garçons' : 'filles'} si ton comité l’accepte.`;
      actions.push({ key: `essais:${g.key}`, label: `Ouvrir les essais ${g.category}`, primary: true, href: '#/essais' }, { key: `mixte:${g.category}`, label: `${g.category} mixte` });
    } else if (up) {
      text = `${g.sure} ${g.sure > 1 ? agree(g.gender, 'sûrs', 'sûres') : agree(g.gender, 'sûr', 'sûre')} : pas assez pour une équipe. Une entente avec un club voisin, ou jouer en ${up.name} (surclassement), si ton comité l’accepte.`;
      actions.push({ key: `entente:${g.key}`, label: 'Chercher une entente', primary: true }, { key: `older:${g.key}:${up.key}`, label: `En ${up.name}` });
    } else {
      text = `${n} ${word} : il en faut 5. Les essais de juin, ou une entente avec un club voisin si ton comité l’accepte.`;
      actions.push({ key: `essais:${g.key}`, label: `Ouvrir les essais ${g.category}`, primary: true, href: '#/essais' }, { key: `entente:${g.key}`, label: 'Chercher une entente' });
    }
    const done = actions.find(a => decisions[a.key] === 'accepted');
    g.warn = { key: g.key, text, actions, decided: done ? 'accepted' : null, choice: done?.label ?? null };
  }

  // --- Regrouper deux catégories voisines du même sexe, toutes deux trop justes (une proposition) ---
  const suggestions: NsSuggestion[] = [];
  const paired = new Set<string>();
  // Les plus grands d'abord (U15 et U17 avant U13 et U15) ; un groupe n'est que dans une suggestion.
  for (const a of [...list].sort((x, y) => rank(y.category) - rank(x.category))) {
    if (a.category === 'Seniors' || a.gender === 'X' || a.rows.length >= 5 || paired.has(a.key)) continue;
    const b = list.find(o => o.gender === a.gender && o.category !== 'Seniors' && rank(o.category) === rank(a.category) + 1 && o.rows.length < 5 && !paired.has(o.key));
    if (!b || a.rows.length + b.rows.length < 5) continue;
    const key = `merge:${a.key}:${b.key}`;
    if (decisions[key] === 'declined') continue;
    paired.add(a.key); paired.add(b.key);
    const g = a.gender;
    suggestions.push({ key, into: b.key, from: a.key, decided: null,
      title: `${a.category} et ${b.category} ${g === 'F' ? 'filles' : 'garçons'} : une seule équipe ?`,
      text: `${a.sure} + ${b.sure} ${agree(g, 'sûrs', 'sûres')}, ${a.rows.length + b.rows.length} avec ceux à confirmer : une ${b.name} avec ${a.rows.length} ${a.category} ${agree(g, 'surclassés', 'surclassées')}${a.coach ? `, et ${a.coach.name} pour la coacher` : ''}, si ton comité l’accepte.` });
    // Le petit groupe ne reçoit pas en plus l'avertissement « il en faut 5 » : la suggestion le couvre.
    if (a.warn && !a.warn.decided) a.warn = null;
    if (b.warn && !b.warn.decided) b.warn = null;
  }

  const open = list.filter(g => g.warn && !g.warn.decided).map(g => ({ key: g.key, title: `${g.name} : à décider`,
    text: `${g.sure} ${playerNoun(g.gender, g.sure)} ${agree(g.gender, 'sûr', 'sûre', g.sure)} : ${g.warn!.actions.map(a => a.label.charAt(0).toLowerCase() + a.label.slice(1)).join(' ou ')}. Ça peut attendre la rentrée.` }))
    .concat(list.filter(g => g.warn?.decided && g.warn.choice?.startsWith('Chercher')).map(g => ({ key: g.key, title: `${g.name} : une entente à chercher`,
      text: 'Avec un club voisin, si ton comité l’accepte. Ça peut attendre la rentrée.' })));
  return { groups: list, out, suggestions, open,
    totals: { teams: list.length, players: list.reduce((n, g) => n + g.rows.length, 0), up: list.reduce((n, g) => n + g.rows.filter(r => r.up).length, 0) } };
}

/** L'instantané des équipes validées (season_plan_validate) : ce que le 1er juillet installe. */
export function snapshot(p: NsProposal): { key: string; name: string; category: string; gender: TeamGender; coach_user: string | null; fee_cents: number | null; people: string[] }[] {
  return p.groups.filter(g => g.rows.length).map(g => ({ key: g.key, name: g.name, category: g.category, gender: g.gender, coach_user: g.coach?.user_id ?? null,
    fee_cents: g.fee_cents, people: g.rows.map(r => r.person_id) }));
}

/** Combien changent de catégorie, sur ceux qui restent (étape 1). */
export function categoryChanges(people: readonly NsPerson[], teams: readonly NsTeam[], season: string): number {
  const endYear = Number(season.slice(5, 9));
  const byId = new Map(teams.map(t => [t.team_id, t]));
  const cats = new Set(teams.map(t => t.category).filter((c): c is string => !!c));
  return people.filter(p => p.project !== 'stop').filter(p => {
    const t = byId.get(p.team_id);
    return !!t?.category && t.category !== 'Seniors' && targetCategory(p, t, endYear, cats) !== t.category;
  }).length;
}

/** Le type d'une catégorie dans l'ordre FFBB (pour les tests). */
export const isCat = (c: string): c is Cat => (CATS as readonly string[]).includes(c);
